/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useRef, useEffect, useState, Fragment } from 'react';
import {
  dropTargetForElements,
  draggable,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { ExpandCollapseIcon } from './ExpandCollapseIcon';

interface DraggableProjectRowProps {
  folder: any;
  level: number;
  isLastChild: boolean;
  parentChain: boolean[];
  isNew: boolean;
  isDuplicate?: boolean;
  duplicateFolderIds?: Set<number>;
  expandedFolders: Set<number>;
  onToggleExpand: (id: number) => void;
  onNest: (draggedId: number, targetId: number | null) => void;
  onRemove: (folderId: number) => void;
  canNest: (draggedId: number, targetId: number) => boolean;
  children?: any[];
  onContextMenu?: (folder: any) => void;
  rightClickedFolderId?: number | null;
}

export const DraggableProjectRow: React.FC<DraggableProjectRowProps> = ({
  folder,
  level,
  isLastChild,
  parentChain,
  isNew,
  isDuplicate,
  duplicateFolderIds,
  expandedFolders,
  onToggleExpand,
  onNest,
  onRemove,
  canNest,
  children,
  onContextMenu,
  rightClickedFolderId,
}) => {
  const styles = useStyles(styleFn);
  const ref = useRef<HTMLTableRowElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);

  const hasChildren = children && children.length > 0;
  const isExpanded = expandedFolders.has(folder.id);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Only make draggable if isNew
    if (!isNew) {
      // Still register as drop target only
      return dropTargetForElements({
        element: el,
        getData: () => ({ folderId: folder.id, type: 'folder-row' }),
        canDrop: ({ source }) => {
          const draggedId = source.data.folderId as number;
          return canNest(draggedId, folder.id);
        },
        onDragEnter: () => setIsDropTarget(true),
        onDragLeave: () => setIsDropTarget(false),
        onDrop: ({ source }) => {
          setIsDropTarget(false);
          const draggedId = source.data.folderId as number;
          onNest(draggedId, folder.id);
        },
      });
    }

    // New folders: draggable + drop target
    return combine(
      draggable({
        element: el,
        getInitialData: () => ({ folderId: folder.id, level }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element: el,
        getData: () => ({ folderId: folder.id, type: 'folder-row' }),
        canDrop: ({ source }) => {
          const draggedId = source.data.folderId as number;
          return canNest(draggedId, folder.id);
        },
        onDragEnter: () => setIsDropTarget(true),
        onDragLeave: () => setIsDropTarget(false),
        onDrop: ({ source }) => {
          setIsDropTarget(false);
          const draggedId = source.data.folderId as number;
          onNest(draggedId, folder.id);
        },
      })
    );
  }, [isNew, folder, canNest, onNest, level]);

  const rowClass = `draggable-row ${isDragging ? 'dragging' : ''} ${
    isDropTarget ? 'drop-target' : ''
  } ${!isNew ? 'existing-folder' : ''}`;

  return (
    <Fragment>
      <tr
        ref={ref}
        className={rowClass}
        css={[
          !isNew && styles.existingRow,
          rightClickedFolderId === folder.id && styles.rightClickedRow,
          isDuplicate && styles.duplicateRow,
        ]}
        onContextMenu={(e) => {
          if (onContextMenu) {
            e.preventDefault();
            onContextMenu(folder);
          }
        }}>
        <td>
          <div css={styles.cellContent}>
            {/* Tree lines container */}
            <div css={styles.treeLines}>
              {level > 0 && (
                <Fragment>
                  {/* Render vertical lines for each ancestor level */}
                  {parentChain.map((isLast, idx) => (
                    <div
                      key={idx}
                      css={[
                        styles.treeLine,
                        !isLast && styles.treeLineVertical,
                      ]}
                    />
                  ))}
                  {/* Current item's connector */}
                  <div
                    css={[
                      styles.treeLine,
                      isLastChild ? styles.treeLineL : styles.treeLineT,
                    ]}
                  />
                </Fragment>
              )}
            </div>

            {/* Chevron container - always present for alignment */}
            <div css={styles.chevronContainer}>
              {hasChildren && (
                <ExpandCollapseIcon
                  isExpanded={isExpanded}
                  onClick={() => onToggleExpand(folder.id)}
                  hasChildren={hasChildren}
                />
              )}
            </div>

            {/* Folder icon - always aligned */}
            <FontAwesomeIcon
              icon={isNew ? ['far', 'folder'] : ['fas', 'folder']}
              css={[
                isNew ? styles.folderIconNew : styles.folderIconExisting,
                isDuplicate && styles.duplicateIcon,
              ]}
            />

            {/* Drag handle - only visible on hover for new folders */}
            {isNew && (
              <FontAwesomeIcon
                icon={['fas', 'grip-vertical']}
                className="drag-handle"
                css={styles.dragHandle}
              />
            )}

            {/* Folder name */}
            <span
              css={[
                styles.folderName,
                !isNew && styles.folderNameDisabled,
                isDuplicate && styles.duplicateFolderName,
              ]}>
              {folder.name}
            </span>
          </div>
        </td>
        <td>
          <span
            css={[styles.description, !isNew && styles.descriptionDisabled]}>
            {folder.description || ''}
          </span>
        </td>
        <td style={{ textAlign: 'center' }}>
          {/* Three-dot action button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onContextMenu) {
                const customEvent = new MouseEvent('contextmenu', {
                  bubbles: true,
                  cancelable: true,
                  view: window,
                  button: 2,
                  buttons: 2,
                  clientX: e.clientX,
                  clientY: e.clientY,
                });
                e.currentTarget.dispatchEvent(customEvent);
              }
            }}
            css={styles.actionButton}>
            <FontAwesomeIcon icon={['far', 'ellipsis-v']} />
          </button>
        </td>
      </tr>
      {/* Render children only if expanded */}
      {isExpanded &&
        children &&
        children.map((child, index) => (
          <DraggableProjectRow
            key={child.id}
            folder={child}
            level={level + 1}
            isLastChild={index === children.length - 1}
            parentChain={level === 0 ? [] : [...parentChain, isLastChild]}
            isNew={child.isNew || false}
            isDuplicate={duplicateFolderIds?.has(child.id) || false}
            duplicateFolderIds={duplicateFolderIds}
            expandedFolders={expandedFolders}
            onToggleExpand={onToggleExpand}
            onNest={onNest}
            onRemove={onRemove}
            canNest={canNest}
            children={child.children}
            onContextMenu={onContextMenu}
            rightClickedFolderId={rightClickedFolderId}
          />
        ))}
    </Fragment>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  cellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 32,
  },
  treeLines: {
    display: 'flex',
    alignItems: 'stretch',
    alignSelf: 'stretch',
    flexShrink: 0,
  },
  treeLine: {
    width: 24,
    position: 'relative',
    flexShrink: 0,
  },
  treeLineVertical: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      bottom: '-4px',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
  },
  treeLineT: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      bottom: '-4px',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 13,
      top: '50%',
      width: '12px !important',
      height: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
      marginTop: '-1px',
    },
  },
  treeLineL: {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 11,
      top: '-4px',
      height: 'calc(50% + 4px)',
      width: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 13,
      top: '50%',
      width: '12px !important',
      height: '2px !important',
      backgroundColor: '#8c8c8c !important',
      opacity: 0.6,
      marginTop: '-1px',
    },
  },
  chevronContainer: {
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  dragHandle: {
    opacity: 0.3,
    transition: 'opacity 0.2s',
    cursor: 'grab',
    fontSize: 12,
    color: theme.colors.textSecondary,
    flexShrink: 0,
  },
  folderIconNew: {
    fontSize: 16,
    color: '#52c41a !important',
    flexShrink: 0,
  },
  folderIconExisting: {
    fontSize: 16,
    color: '#faad14 !important',
    opacity: '0.5 !important',
    flexShrink: 0,
    '& svg': {
      color: '#faad14 !important',
    },
    '& svg path': {
      fill: '#faad14 !important',
    },
  },
  folderName: {
    flex: 1,
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.text.primary,
  },
  folderNameDisabled: {
    color: 'rgba(0, 0, 0, 0.45)',
    fontWeight: 400,
  },
  description: {
    color: theme.colors.textSecondary,
    fontSize: 14,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  descriptionDisabled: {
    color: 'rgba(0, 0, 0, 0.45)',
  },
  removeButton: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '4px 8px',
    height: '24px',
    minWidth: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    color: theme.colors.textSecondary || '#8c8c8c',
    '&:hover': {
      backgroundColor: 'rgba(255, 77, 79, 0.1)',
      color: '#ff4d4f',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '& svg': {
      fontSize: '14px',
      color: 'inherit',
    },
  },
  actionButton: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: '4px 8px',
    height: '24px',
    minWidth: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    fontSize: '16px',
    color: theme.colors.textSecondary || '#8c8c8c',
    '&:hover': {
      backgroundColor: theme.colors.primaryLight || 'rgba(24, 144, 255, 0.1)',
      color: theme.colors.primary,
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
    '& svg': {
      fontSize: '16px',
      color: 'inherit',
    },
  },
  existingRow: {
    backgroundColor: theme.colors.backgroundDisabled || '#f5f5f5',
    cursor: 'default',
    '&:hover': {
      backgroundColor: theme.colors.backgroundDisabled || '#f5f5f5',
    },
  },
  rightClickedRow: {
    borderBottom: `2px solid ${theme.colors.primary} !important`,
    position: 'relative',
  },
  duplicateRow: {
    backgroundColor: 'rgba(255, 77, 79, 0.1) !important',
    '&:hover': {
      backgroundColor: 'rgba(255, 77, 79, 0.15) !important',
    },
  },
  duplicateFolderName: {
    color: '#ff4d4f !important',
    fontWeight: 600,
  },
  duplicateIcon: {
    '& svg path': {
      fill: '#ff4d4f !important',
    },
  },
});
